import {Progress} from "antd";
import React from "react";
import {GiTwoCoins} from "react-icons/gi";
import {formatCreditsCompact} from "../../utils/FormatUtils";
import * as styles from "./AiCreditUsageIndicator.module.css";

const STEP_COUNT = 5;
const CREDIT_USAGE_FULL_PERCENT = 100;
const CREDIT_USAGE_WARNING_PERCENT = 80;
const CREDIT_USAGE_ACTIVE_PERCENT = 50;

const CREDIT_USAGE_COLORS = {
    full: "#ff4d4f",
    warning: "#faad14",
    active: "#52c41a",
    normal: "#3d8bfd",
};

type Props = {
    used: number;
    limit: number;
    label?: string;
};

function AiCreditUsageIndicator(props: Props) {
    const percent = creditPercent(props.used, props.limit);
    const usedText = formatCreditAmount(props.used);
    const limitText = formatCreditAmount(props.limit);

    return (
        <span className={styles.root}>
            {props.label && <span className={styles.label}>{props.label}</span>}
            <Progress
                className={styles.progress}
                percent={Math.min(percent, CREDIT_USAGE_FULL_PERCENT)}
                steps={STEP_COUNT}
                strokeColor={stepColors(percent)}
                showInfo={false}
                title={`${usedText} / ${limitText} credits (${percent.toFixed(1)}%)`}
            />
            <span className={styles.value}>
                <GiTwoCoins aria-hidden={true} className={styles.icon}/>
                {creditText(usedText, limitText)}
            </span>
        </span>
    );
}

function creditPercent(used: number, limit: number) {
    if (!limit || !used) {
        return 0;
    }

    return (used / limit) * 100;
}

function creditColor(percent: number) {
    if (percent >= CREDIT_USAGE_FULL_PERCENT) {
        return CREDIT_USAGE_COLORS.full;
    }

    if (percent >= CREDIT_USAGE_WARNING_PERCENT) {
        return CREDIT_USAGE_COLORS.warning;
    }

    if (percent >= CREDIT_USAGE_ACTIVE_PERCENT) {
        return CREDIT_USAGE_COLORS.active;
    }

    return CREDIT_USAGE_COLORS.normal;
}

function stepColors(percent: number) {
    return Array.from({length: STEP_COUNT}, () => creditColor(percent));
}

function creditText(used: string, limit: string) {
    return `${used} out of ${limit} credits`;
}

function formatCreditAmount(value: number) {
    return formatCreditsCompact(value).replace(/\s*credits$/, "");
}

export default AiCreditUsageIndicator;
